// Adapted from herdr voice.ts, Copyright (c) 2026 devswha (MIT).
import { useEffect, useRef, useState } from 'react';
import { createVoiceController, type Utterance, type VoiceOptions, type VoiceState } from './controller';
import { useVoiceStatus } from './status';

export function useVoiceCapture(options: VoiceOptions) {
  const status = useVoiceStatus();
  const latest = useRef({ options, status });
  latest.current = { options, status };
  const mounted = useRef(true);
  const levelRef = useRef<HTMLElement | null>(null);
  const [state, setState] = useState<VoiceState>({ active: false, starting: false, busy: false, error: null, lastText: '' });
  const [controller] = useState(() => createVoiceController({
    options: () => latest.current.options,
    status: () => latest.current.status,
    meter: () => levelRef.current,
    changed: (next) => { if (mounted.current) setState(next); },
  }));
  useEffect(() => {
    mounted.current = true;
    const onHidden = (): void => { if (document.visibilityState === 'hidden') void controller.stop(); };
    const onPageHide = (): void => { void controller.stop(); };
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      mounted.current = false;
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onPageHide);
      void controller.stop();
    };
  }, [controller]);
  useEffect(() => () => { void controller.stop(); }, [controller, options.lessonId]);
  return { ...state, levelRef, ...controller };
}

/** stop() drains recorder output, ordered transcriptions, and async onUtterance saves. */
export function useThinkAloud(options: {
  lessonId: string;
  now(): number;
  onUtterance(utterance: Utterance): void | Promise<void>;
}) {
  return useVoiceCapture({ ...options, continuous: true });
}
